import { useState } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Send, Sparkles, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIAdvisor({ user, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages([...messages, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai/advisor`,
        { message: input },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000 // 60 second timeout
        }
      );

      const aiMessage = { role: 'assistant', content: response.data.response };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI Error:', error);
      
      // Add error message to chat
      const errorMessage = { 
        role: 'assistant', 
        content: 'Sorry, I\'m having trouble connecting to the AI service right now. Please try again in a moment.' 
      };
      setMessages((prev) => [...prev, errorMessage]);
      
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try a shorter question.');
      } else {
        toast.error('Failed to get AI response. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setInput('');
    toast.success('Started a new conversation');
  };

  const suggestedQuestions = [
    'How can I improve my savings?',
    'What\'s my spending pattern?',
    'Should I invest more?',
    'How can I reduce expenses?'
  ];

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900" data-testid="ai-advisor-page">
      <div className="max-w-5xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/ai-advisor" />

        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100" data-testid="ai-advisor-title">
                Ask FinAI
              </h1>
            </div>
            {messages.length > 0 && (
              <Button
                onClick={handleNewChat}
                variant="outline"
                className="flex items-center gap-2 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                data-testid="new-chat-button"
              >
                <Plus className="w-4 h-4" />
                New Chat
              </Button>
            )}
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-15">Get personalized financial advice powered by AI</p>
        </div>

        <Card className="glass p-6 mb-6 dark:bg-gray-800" data-testid="chat-container">
          <div className="h-[500px] overflow-y-auto mb-6 space-y-4" data-testid="messages-container">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Sparkles className="w-16 h-16 text-purple-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">Welcome to AI Financial Advisor!</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">Ask me anything about your finances</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-2xl">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => setInput(question)}
                      className="p-3 bg-white dark:bg-gray-700 rounded-xl hover:shadow-lg transition-all text-left text-sm text-gray-700 dark:text-gray-300"
                      data-testid={`suggested-question-${index}`}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${index}`}
                >
                  <div
                    className={`max-w-[80%] p-4 rounded-2xl ${
                      message.role === 'user'
                        ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 shadow-md'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex items-center space-x-2 mb-2">
                        <Sparkles className="w-4 h-4 text-purple-500" />
                        <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">AI Advisor</span>
                      </div>
                    )}
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex justify-start" data-testid="loading-message">
                <div className="max-w-[80%] p-4 rounded-2xl bg-white dark:bg-gray-700 shadow-md">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                    <span className="text-gray-600 dark:text-gray-400">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Show suggestions after messages OR when empty */}
          {messages.length > 0 && !loading && (
            <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">💡 Try asking:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => setInput(question)}
                    className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-all text-left text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
                    data-testid={`suggested-question-${index}`}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask me about your finances..."
              className="resize-none dark:bg-gray-700 dark:text-gray-100"
              rows={3}
              data-testid="message-input"
            />
            <Button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white h-auto px-6"
              data-testid="send-button"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
        </Card>

        <Card className="glass p-6 dark:bg-gray-800" data-testid="tips-card">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">Tips for better advice:</h3>
          <ul className="space-y-2 text-gray-600 dark:text-gray-400 text-sm">
            <li>• Be specific about your financial goals</li>
            <li>• Ask about spending patterns in specific categories</li>
            <li>• Request recommendations for budget optimization</li>
            <li>• Inquire about investment strategies based on your portfolio</li>
          </ul>
        </Card>
      </div>
      <Footer />
    </div>
  );
}